import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ListSubheader,
  MenuItem,
} from "@mui/material";
import { AlmanacConfig } from "protos/almanac/almanac";
import { type AlmanacGroup } from "portal/components/categoryTemplates/useAlmanacOptions";
import { Field, useField } from "formik";
import { Select } from "formik-mui";
import { Trans, useTranslation } from "react-i18next";
import CarbonLogo from "portal/images/logo_icon.svg?react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { FunctionComponent, useMemo } from "react";

interface AlmanacSelectorFieldProps {
  almanacGroups: AlmanacGroup[];
  name: string;
}

const AlmanacLabel: FunctionComponent<{
  almanac: AlmanacConfig;
  isCarbon?: boolean;
  robotSerial?: string;
}> = ({ almanac, isCarbon = false, robotSerial }) => (
  <span className="flex items-center gap-1">
    {isCarbon ? (
      <>
        <CarbonLogo className="h-3 w-3 shrink-0 text-carbon-orange" />
        {almanac.name}
      </>
    ) : robotSerial ? (
      `${robotSerial}: ${almanac.name}`
    ) : (
      almanac.name
    )}
  </span>
);

const findAlmanacInGroups = (
  almanacGroups: AlmanacGroup[],
  almanacId: string
):
  | { almanac: AlmanacConfig; isCarbon: boolean; robotSerial?: string }
  | undefined =>
  almanacGroups
    .flatMap((group) =>
      group.almanacs.map((almanac) => ({
        almanac,
        isCarbon: group.isCarbon ?? false,
        ...(!group.isCarbon && group.label ? { robotSerial: group.label } : {}),
      }))
    )
    .find((entry) => entry.almanac.id === almanacId);

export const AlmanacSelectorField: FunctionComponent<
  AlmanacSelectorFieldProps
> = ({ almanacGroups, name }) => {
  const { t } = useTranslation();
  const [field] = useField<string>(name);

  const selectedAlmanac = useMemo(
    () => findAlmanacInGroups(almanacGroups, field.value),
    [almanacGroups, field.value]
  );

  const renderSelectedAlmanac = (almanacId: string): React.ReactNode => {
    const entry = findAlmanacInGroups(almanacGroups, almanacId);
    return entry ? (
      <AlmanacLabel
        almanac={entry.almanac}
        isCarbon={entry.isCarbon}
        robotSerial={entry.robotSerial}
      />
    ) : (
      almanacId
    );
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {selectedAlmanac ? (
          <Trans
            i18nKey="components.categoryTemplates.fields.sourceAlmanacSelected"
            components={[
              <AlmanacLabel
                key="0"
                almanac={selectedAlmanac.almanac}
                isCarbon={selectedAlmanac.isCarbon}
                robotSerial={selectedAlmanac.robotSerial}
              />,
            ]}
          />
        ) : (
          t("components.categoryTemplates.fields.sourceAlmanac")
        )}
      </AccordionSummary>
      <AccordionDetails className="p-0 pt-2">
        <Field
          component={Select}
          name={name}
          label={t("models.almanacs.almanac_one_title")}
          formControl={{ fullWidth: true }}
          renderValue={renderSelectedAlmanac}
        >
          {almanacGroups.flatMap((group) => [
            group.label ? (
              <ListSubheader
                key={`header-${group.isCarbon ? "__global__" : group.label}`}
                className="flex items-center gap-2 uppercase text-sm"
              >
                {group.isCarbon && <CarbonLogo className="h-3" />}
                {group.label}
              </ListSubheader>
            ) : undefined,
            ...group.almanacs.map((almanac: AlmanacConfig) => (
              <MenuItem key={almanac.id} value={almanac.id}>
                <AlmanacLabel almanac={almanac} isCarbon={group.isCarbon} />
              </MenuItem>
            )),
          ])}
        </Field>
      </AccordionDetails>
    </Accordion>
  );
};
